import React, { useEffect, useMemo, useState } from "react";
import { hotelApi } from "./hotelApi";

export default function HotelCheckInScreen({
  onBack,
  onOpenSupplies,
}) {
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [guests, setGuests] = useState([]);
  const [stays, setStays] = useState([]);
  const [supplies, setSupplies] = useState([]);

  const [guestId, setGuestId] = useState(0);
  const [guestName, setGuestName] = useState("");
  const [phone, setPhone] = useState("");
  const [idNumber, setIdNumber] = useState("");
  const [pin, setPin] = useState("");
  const [roomId, setRoomId] = useState(0);
  const [roomNo, setRoomNo] = useState("");
  const [roomType, setRoomType] = useState("");

  const [breakfastIncluded, setBreakfastIncluded] = useState(false);
  const [nightlyRate, setNightlyRate] = useState("");
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [expectedCheckout, setExpectedCheckout] = useState("");

  const [cashAmount, setCashAmount] = useState("");
  const [mpesaAmount, setMpesaAmount] = useState("");
  const [bankAmount, setBankAmount] = useState("");

  const [cartLines, setCartLines] = useState([]);

  const [showSupplyModal, setShowSupplyModal] = useState(false);
  const [modalSelectedSupplyId, setModalSelectedSupplyId] = useState("");
  const [modalQty, setModalQty] = useState(1);
  const [modalCart, setModalCart] = useState([]);

  const [showMpesaModal, setShowMpesaModal] = useState(false);
  const [mpesaLoading, setMpesaLoading] = useState(false);
  const [mpesaRows, setMpesaRows] = useState([]);
  const [selectedMpesaRow, setSelectedMpesaRow] = useState(null);

  const showMsg = (m) => {
    setMsg(m);
    clearTimeout(showMsg._t);
    showMsg._t = setTimeout(() => setMsg(""), 5000);
  };

  const activeStatuses = useMemo(() => new Set(["checked_in", "active"]), []);

  const selectedRoom = useMemo(() => {
    if (!roomId) return null;
    return rooms.find((r) => Number(r.id) === Number(roomId)) || null;
  }, [rooms, roomId]);

  const selectedGuest = useMemo(() => {
    if (!guestId) return null;
    return guests.find((g) => Number(g.id) === Number(guestId)) || null;
  }, [guests, guestId]);

  const activeStays = useMemo(() => {
    return stays.filter((s) =>
      activeStatuses.has(String(s.status || "").toLowerCase())
    );
  }, [stays, activeStatuses]);


  const roomOccupiedMap = useMemo(() => {
    const map = new Map();

    activeStays.forEach((s) => {
      if (s.room_id != null) map.set(`id:${Number(s.room_id)}`, true);
      if (s.room_no != null && String(s.room_no).trim() !== "") {
        map.set(`no:${String(s.room_no).trim()}`, true);
      }
    });

    rooms.forEach((r) => {
      const occupied =
        String(r.status || "").toLowerCase() === "occupied" ||
        Number(r.is_occupied) === 1;
      if (occupied) {
        map.set(`id:${Number(r.id)}`, true);
        map.set(`no:${String(r.room_no || "").trim()}`, true);
      }
    });

    return map;
  }, [activeStays, rooms]);

  const isRoomOccupied = (room) => {
    if (!room) return false;
    return (
      roomOccupiedMap.get(`id:${Number(room.id)}`) === true ||
      roomOccupiedMap.get(`no:${String(room.room_no || "").trim()}`) === true
    );
  };


  const availableRooms = useMemo(() => {
    return rooms.filter((r) => !isRoomOccupied(r) && Number(r.is_active) === 1);
  }, [rooms, roomOccupiedMap]);

  const occupiedRoomsCount = useMemo(() => {
    return rooms.filter((r) => isRoomOccupied(r)).length;
  }, [rooms, roomOccupiedMap]);

  const selectedRoomOccupied = selectedRoom ? isRoomOccupied(selectedRoom) : false;

  const activeGuestStay = useMemo(() => {
    if (guestId > 0) {
      return (
        activeStays.find((s) => Number(s.guest_id) === Number(guestId)) || null
      );
    }

    if (guestName.trim()) {
      const name = guestName.trim().toLowerCase();
      return (
        activeStays.find(
          (s) => String(s.guest_name || "").trim().toLowerCase() === name
        ) || null
      );
    }

    return null;
  }, [activeStays, guestId, guestName]);

  const canBuildDraft = useMemo(() => {
    const hasGuest = guestId > 0 || guestName.trim() !== "";
    const hasRoom = roomId > 0;
    const hasRate = String(nightlyRate).trim() !== "" && Number(nightlyRate) > 0;
    return hasGuest && hasRoom && hasRate;
  }, [guestId, guestName, roomId, nightlyRate]);

  const numericCash = useMemo(() => Number(cashAmount || 0), [cashAmount]);
  const numericMpesa = useMemo(() => Number(mpesaAmount || 0), [mpesaAmount]);
  const numericBank = useMemo(() => Number(bankAmount || 0), [bankAmount]);

  const amountPaid = useMemo(() => {
    return numericCash + numericMpesa + numericBank;
  }, [numericCash, numericMpesa, numericBank]);

  const paymentModesUsed = useMemo(() => {
    const used = [];
    if (numericCash > 0) used.push("cash");
    if (numericMpesa > 0) used.push("mpesa");
    if (numericBank > 0) used.push("bank");
    return used;
  }, [numericCash, numericMpesa, numericBank]);

  const paymentModeLabel = useMemo(() => {
    if (paymentModesUsed.length === 0) return "unpaid";
    if (paymentModesUsed.length === 1) return paymentModesUsed[0];
    return "mixed";
  }, [paymentModesUsed]);

  const supplyById = useMemo(() => {
    const map = new Map();
    supplies.forEach((s) => map.set(Number(s.id), s));
    return map;
  }, [supplies]);

  const selectedModalSupply = useMemo(() => {
    return (
      supplies.find((s) => Number(s.id) === Number(modalSelectedSupplyId)) || null
    );
  }, [supplies, modalSelectedSupplyId]);

  const roomLine = useMemo(() => {
    const rate = Number(nightlyRate || 0);
    return {
      code: "ROOM",
      type: "room",
      name: `Room Charge${roomNo ? ` - ${roomNo}` : ""}`,
      qty: 1,
      unit_price: rate,
      line_total: rate,
    };
  }, [nightlyRate, roomNo]);

  const breakfastLine = useMemo(() => {
    if (!breakfastIncluded) return null;
    return {
      code: "BREAKFAST",
      type: "service",
      name: "Breakfast Included",
      qty: 1,
      unit_price: 0,
      line_total: 0,
    };
  }, [breakfastIncluded]);

  const cartSubTotal = useMemo(() => {
    const base = cartLines.reduce(
      (sum, row) => sum + Number(row.line_total || 0),
      0
    );
    const room = Number(roomLine.line_total || 0);
    const breakfast = Number(breakfastLine?.line_total || 0);
    return base + room + breakfast;
  }, [cartLines, roomLine, breakfastLine]);

  const suppliesTotal = useMemo(() => {
    return cartLines
      .filter((x) => x.type === "supply")
      .reduce((sum, row) => sum + Number(row.line_total || 0), 0);
  }, [cartLines]);

  const balancePreview = useMemo(() => {
    return cartSubTotal - amountPaid;
  }, [cartSubTotal, amountPaid]);

  const checkoutDraft = useMemo(() => {
    return [roomLine, ...(breakfastLine ? [breakfastLine] : []), ...cartLines];
  }, [roomLine, breakfastLine, cartLines]);

  const modalCartTotal = useMemo(() => {
    return modalCart.reduce((sum, row) => sum + Number(row.line_total || 0), 0);
  }, [modalCart]);

  const loadRooms = async () => {
    const res = await hotelApi.get("/rooms");
    setRooms(Array.isArray(res.data?.rows) ? res.data.rows : []);
  };

  const loadGuests = async () => {
    const res = await hotelApi.get("/guests");
    setGuests(Array.isArray(res.data?.rows) ? res.data.rows : []);
  };

  const loadStays = async () => {
    const res = await hotelApi.get("/stays");
    setStays(Array.isArray(res.data?.rows) ? res.data.rows : []);
  };

  const loadSupplies = async () => {
    const res = await hotelApi.get("/supplies");
    setSupplies(Array.isArray(res.data?.rows) ? res.data.rows : []);
  };

  const loadMpesaTransactions = async () => {
    try {
      setMpesaLoading(true);
      const res = await hotelApi.get("/mpesa");
      setMpesaRows(Array.isArray(res.data?.rows) ? res.data.rows : []);
    } catch (e) {
      console.error(e);
      showMsg(
        "❌ " +
          (e?.response?.data?.message ||
            e?.response?.data?.error ||
            "Failed to load M-Pesa transactions.")
      );
    } finally {
      setMpesaLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setMsg("");
        await Promise.all([loadRooms(), loadGuests(), loadStays(), loadSupplies()]);
      } catch (e) {
        console.error(e);
        showMsg(
          `❌ ${
            e?.response?.data?.message ||
            e?.response?.data?.error ||
            "Failed to initialize hotel check-in."
          }`
        );
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedRoom) return;
    setRoomNo(String(selectedRoom.room_no || ""));
    setRoomType(String(selectedRoom.room_type || ""));
    if (nightlyRate === "" || Number(nightlyRate) === 0) {
      setNightlyRate(String(selectedRoom.base_rate ?? ""));
    }
  }, [selectedRoom, nightlyRate]);

useEffect(() => {
  if (!selectedGuest) return;

  setGuestName(String(selectedGuest.full_name || selectedGuest.guest_name || ""));
  setPhone(String(selectedGuest.phone || ""));
  setIdNumber(String(selectedGuest.id_number || ""));
  setPin(String(selectedGuest.pin || ""));
}, [selectedGuest]);

  const refreshAll = async () => {
    try {
      setLoading(true);
      await Promise.all([loadRooms(), loadGuests(), loadStays(), loadSupplies()]);
    } catch (e) {
      console.error(e);
      showMsg(
        `❌ ${
          e?.response?.data?.message ||
          e?.response?.data?.error ||
          "Refresh failed."
        }`
      );
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setGuestId(0);
    setGuestName("");
    setPhone("");
    setIdNumber("");
    setRoomId(0);
    setRoomNo("");
    setRoomType("");
    setBreakfastIncluded(false);
    setNightlyRate("");
    setAdults(1);
    setChildren(0);
    setExpectedCheckout("");
    setCashAmount("");
    setMpesaAmount("");
    setBankAmount("");
    setCartLines([]);
    setShowSupplyModal(false);
    setModalSelectedSupplyId("");
    setModalQty(1);
    setModalCart([]);
    setShowMpesaModal(false);
    setMpesaRows([]);
    setSelectedMpesaRow(null);
  };

  const openSupplyModal = () => {
    const existingSupplyLines = cartLines
      .filter((x) => x.type === "supply")
      .map((x) => ({ ...x }));

    setModalCart(existingSupplyLines);
    setModalSelectedSupplyId("");
    setModalQty(1);
    setShowSupplyModal(true);
  };

  const closeSupplyModal = () => {
    setShowSupplyModal(false);
    setModalSelectedSupplyId("");
    setModalQty(1);
  };

  const addSupplyToModalCart = () => {
    if (!selectedModalSupply) {
      showMsg("⚠️ Select an item.");
      return;
    }

    const qty = Math.max(1, Number(modalQty || 1));
    const stock = Number(selectedModalSupply.qty_on_hand || 0);

    if (qty > stock) {
      showMsg("⚠️ Quantity exceeds stock on hand.");
      return;
    }

    setModalCart((prev) => {
      const existing = prev.find(
        (x) => Number(x.supply_id) === Number(selectedModalSupply.id)
      );

      if (existing) {
        const newQty = Number(existing.qty) + qty;
        if (newQty > stock) {
          showMsg("⚠️ Combined quantity exceeds stock on hand.");
          return prev;
        }

        return prev.map((x) =>
          Number(x.supply_id) === Number(selectedModalSupply.id)
            ? {
                ...x,
                qty: newQty,
                line_total: newQty * Number(x.unit_price || 0),
              }
            : x
        );
      }

      return [
        ...prev,
        {
          type: "supply",
          supply_id: Number(selectedModalSupply.id),
          name: selectedModalSupply.item_name,
          qty,
          unit_price: Number(selectedModalSupply.unit_price || 0),
          line_total: qty * Number(selectedModalSupply.unit_price || 0),
        },
      ];
    });

    setModalSelectedSupplyId("");
    setModalQty(1);
  };

  const updateModalCartQty = (index, qtyValue) => {
    const qty = Math.max(1, Number(qtyValue || 1));

    setModalCart((prev) => {
      const row = prev[index];
      if (!row) return prev;

      const stockRow = supplyById.get(Number(row.supply_id));
      const stock = Number(stockRow?.qty_on_hand || 0);

      if (qty > stock) {
        showMsg("⚠️ Quantity exceeds stock on hand.");
        return prev;
      }

      return prev.map((r, i) =>
        i === index
          ? {
              ...r,
              qty,
              line_total: qty * Number(r.unit_price || 0),
            }
          : r
      );
    });
  };

  const removeModalCartLine = (index) => {
    setModalCart((prev) => prev.filter((_, i) => i !== index));
  };

  const applySupplyModalCart = () => {
    setCartLines((prev) => {
      const nonSupplies = prev.filter((x) => x.type !== "supply");
      return [...nonSupplies, ...modalCart];
    });
    setShowSupplyModal(false);
    setModalSelectedSupplyId("");
    setModalQty(1);
    showMsg("✅ Supplies applied to draft cart.");
  };

  const openMpesaModal = async () => {
    setShowMpesaModal(true);
    await loadMpesaTransactions();
  };

  const closeMpesaModal = () => {
    setShowMpesaModal(false);
  };

const pickMpesaTransaction = (row) => {
  const amt = Number(row.amount || 0);

  setMpesaAmount(String(amt));
  setSelectedMpesaRow(row);

  setShowMpesaModal(false);
  setShowPaymentModal(true);

  showMsg(`✅ M-Pesa payment ${amt.toFixed(2)} selected`);
};

  const updateCartQty = (index, qtyValue) => {
    const qty = Math.max(1, Number(qtyValue || 1));

    setCartLines((prev) => {
      const row = prev[index];
      if (!row || row.type !== "supply") return prev;

      const stockRow = supplyById.get(Number(row.supply_id));
      const stock = Number(stockRow?.qty_on_hand || 0);

      if (qty > stock) {
        showMsg("⚠️ Quantity exceeds stock on hand.");
        return prev;
      }

      return prev.map((r, i) =>
        i === index
          ? {
              ...r,
              qty,
              line_total: qty * Number(r.unit_price || 0),
            }
          : r
      );
    });
  };

  const removeCartLine = (index) => {
    setCartLines((prev) => prev.filter((_, i) => i !== index));
  };

  const clearDraftCart = () => {
    setCartLines([]);
    setCashAmount("");
    setMpesaAmount("");
    setBankAmount("");
    setSelectedMpesaRow(null);
    showMsg("🗑 Draft cart cleared.");
  };

  const escapeHtml = (value) => {
    return String(value ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;");
  };

  const toMysqlDateTime = (value) => {
    if (!value) return null;
    return String(value).replace("T", " ") + ":00";
  };

  const printReceipt = (receipt) => {
    const w = window.open("", "_blank", "width=360,height=720");
    if (!w) {
      showMsg("⚠️ Popup blocked. Allow popups to print receipt.");
      return;
    }

    const money = (value) => Number(value || 0).toFixed(2);

    w.document.write(`
      <html>
        <head>
          <title>Hotel Receipt</title>
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <style>
            @page {
              size: 80mm auto;
              margin: 0;
            }

            * {
              box-sizing: border-box;
            }

            html,
            body {
              margin: 0;
              padding: 0;
              background: #fff;
              color: #000;
              font-family: Arial, Helvetica, sans-serif;
              font-size: 11px;
              line-height: 1.25;
              width: 100%;
            }

            .receipt {
              width: 72mm;
              max-width: 72mm;
              margin: 0 auto;
              padding: 4mm 3mm;
            }

            .center {
              text-align: center;
            }

            h2 {
              margin: 0 0 3mm;
              font-size: 15px;
              line-height: 1.1;
              font-weight: 800;
              text-transform: uppercase;
            }

            .sub-title {
              font-size: 11px;
              margin-bottom: 3mm;
            }

            .rule {
              border-top: 1px dashed #000;
              margin: 3mm 0;
            }

            .line {
              display: flex;
              justify-content: space-between;
              gap: 6px;
              margin: 1.5mm 0;
              word-break: break-word;
            }

            .line span:first-child {
              font-weight: 700;
              flex: 0 0 auto;
            }

            .line span:last-child {
              text-align: right;
              flex: 1;
            }

            table {
              width: 100%;
              border-collapse: collapse;
              table-layout: fixed;
              margin-top: 2mm;
            }

            th,
            td {
              padding: 1.2mm 0;
              vertical-align: top;
              font-size: 10.5px;
            }

            th {
              border-bottom: 1px dashed #000;
              font-weight: 800;
              text-align: left;
            }

            td {
              border-bottom: 1px dotted #aaa;
            }

            .item-col {
              width: 43%;
              word-break: break-word;
              white-space: normal;
            }

            .qty-col {
              width: 14%;
              text-align: right;
            }

            .price-col,
            .total-col {
              width: 21.5%;
              text-align: right;
            }

            .total-line {
              font-weight: 800;
              font-size: 12px;
            }

            .balance-line {
              font-weight: 900;
              font-size: 13px;
            }

            .footer {
              margin-top: 4mm;
              text-align: center;
              font-size: 10px;
            }

            @media print {
              html,
              body {
                width: 80mm;
              }

              .receipt {
                width: 72mm;
                max-width: 72mm;
                margin: 0;
                padding: 3mm;
              }

              button {
                display: none !important;
              }
            }

            @media screen and (max-width: 420px) {
              .receipt {
                width: 100%;
                max-width: 100%;
              }
            }
          </style>
        </head>
        <body>
          <div class="receipt">
            <div class="center">
              <h2>Hotel Receipt</h2>
              <div class="sub-title">Check-In Receipt</div>
            </div>

            <div class="rule"></div>

            <div class="line"><span>Guest</span><span>${escapeHtml(receipt.guest_name || "-")}</span></div>
            <div class="line"><span>Room</span><span>${escapeHtml(receipt.room_no || "-")}</span></div>
            <div class="line"><span>Type</span><span>${escapeHtml(receipt.room_type || "-")}</span></div>
            <div class="line"><span>Date</span><span>${escapeHtml(receipt.checkin_at || "-")}</span></div>
            <div class="line"><span>Payment</span><span>${escapeHtml(receipt.payment_mode || "-")}</span></div>

            <div class="rule"></div>

            <table>
              <thead>
                <tr>
                  <th class="item-col">Item</th>
                  <th class="qty-col">Qty</th>
                  <th class="price-col">Price</th>
                  <th class="total-col">Total</th>
                </tr>
              </thead>
              <tbody>
                ${(receipt.lines || [])
                  .map(
                    (row) => `
                      <tr>
                        <td class="item-col">${escapeHtml(row.name)}</td>
                        <td class="qty-col">${money(row.qty)}</td>
                        <td class="price-col">${money(row.unit_price)}</td>
                        <td class="total-col">${money(row.line_total)}</td>
                      </tr>
                    `
                  )
                  .join("")}
              </tbody>
            </table>

            <div class="rule"></div>

            <div class="line total-line"><span>Subtotal</span><span>${money(receipt.subtotal)}</span></div>
            <div class="line"><span>Cash</span><span>${money(receipt.cash_amount)}</span></div>
            <div class="line"><span>M-Pesa</span><span>${money(receipt.mpesa_amount)}</span></div>
            <div class="line"><span>Bank</span><span>${money(receipt.bank_amount)}</span></div>
            <div class="line total-line"><span>Paid</span><span>${money(receipt.amount_paid)}</span></div>
            <div class="line balance-line"><span>Balance</span><span>${money(receipt.balance)}</span></div>

            <div class="rule"></div>
            <div class="footer">Thank you. Please keep this receipt.</div>
          </div>

          <script>
            window.onload = function () {
              setTimeout(function () {
                window.focus();
                window.print();
              }, 250);
            };
          </script>
        </body>
      </html>
    `);

    w.document.close();
  };

const finalizeCheckIn = async (e) => {
  e?.preventDefault?.();

    if (!canBuildDraft) {
      showMsg("⚠️ Guest, Room, and Rate are required.");
      return;
    }

    if (!selectedRoom) {
      showMsg("⚠️ Select a valid room.");
      return;
    }

    if (isRoomOccupied(selectedRoom)) {
      showMsg("⚠️ Selected room is already occupied.");
      return;
    }

    if (activeGuestStay?.id) {
      showMsg(
        `⚠️ This guest already has an active stay in room ${activeGuestStay.room_no || "-"}`
      );
      return;
    }

    if (numericCash < 0 || numericMpesa < 0 || numericBank < 0) {
      showMsg("⚠️ Payment values cannot be negative.");
      return;
    }

    try {
      setLoading(true);
      setMsg("");

      const payload = {
        guest_id: guestId > 0 ? Number(guestId) : 0,
        room_id: Number(roomId),
        guest_name: guestName.trim(),
        phone: phone.trim(),
        id_number: idNumber.trim(),
         pin: pin.trim(),
        room_no: String(roomNo || "").trim(),
        nightly_rate: Number(nightlyRate || 0),
        adults: Math.max(1, Number(adults || 1)),
        children: Math.max(0, Number(children || 0)),
        status: "checked_in",
        expected_checkout: toMysqlDateTime(expectedCheckout),
        amount_paid: Number(amountPaid || 0),
        balance: Number(balancePreview || 0),
        breakfast_included: breakfastIncluded ? 1 : 0,
        room_type: roomType.trim() || null,
        supplies_total: Number(suppliesTotal || 0),
        grand_total: Number(cartSubTotal || 0),
        total_amount: Number(cartSubTotal || 0),
        notes: [
          phone.trim() ? `Phone: ${phone.trim()}` : "",
          idNumber.trim() ? `ID/Passport: ${idNumber.trim()}` : "",
          `Payment Mode: ${paymentModeLabel}`,
          `Cash: ${numericCash.toFixed(2)}`,
          `Mpesa: ${numericMpesa.toFixed(2)}`,
          `Bank: ${numericBank.toFixed(2)}`,
          selectedMpesaRow?.mpesa_code ? `Mpesa Code: ${selectedMpesaRow.mpesa_code}` : ""
        ]
          .filter(Boolean)
          .join(" | "),
      };

      const stayRes = await hotelApi.post("/stays", payload);

      if (stayRes.data?.success === false) {
        showMsg("❌ " + (stayRes.data?.error || stayRes.data?.message || "Check-in failed"));
        return;
      }

      const stayId = Number(
        stayRes.data?.rows?.[0]?.stay_id ||
          stayRes.data?.rows?.[0]?.id ||
          stayRes.data?.stay_id ||
          stayRes.data?.id ||
          0
      );

      if (stayId > 0 && cartLines.length > 0) {
        await hotelApi.post("/supplies/sell", {
          stay_id: stayId,
          payment_method: "room_charge",
          notes: null,
          items: cartLines
            .filter((x) => x.type === "supply")
            .map((x) => ({
              supply_id: Number(x.supply_id),
              qty: Number(x.qty),
            })),
        });
      }

      if (selectedMpesaRow?.id) {
        await hotelApi.post(`/mpesa/${selectedMpesaRow.id}/counted`, {
          counted: 1,
          stay_id: stayId > 0 ? stayId : null,
        });
      }

      const receipt = {
        guest_name: guestName.trim(),
        room_no: roomNo,
        room_type: roomType.trim(),
        checkin_at: new Date().toLocaleString(),
        payment_mode: paymentModeLabel,
        lines: checkoutDraft,
        subtotal: cartSubTotal,
        cash_amount: numericCash,
        mpesa_amount: numericMpesa,
        bank_amount: numericBank,
        amount_paid: amountPaid,
        balance: balancePreview,
      };

      showMsg("✅ Check-in saved successfully");
      printReceipt(receipt);
      resetForm();
      await refreshAll();
    } catch (e) {
      console.error(e);
      showMsg(
        "❌ " +
          (e?.response?.data?.message ||
            e?.response?.data?.error ||
            "Final save failed.")
      );
    } finally {
      setLoading(false);
    }
  };

  const doCheckout = async (stayId) => {
    const ok = window.confirm("Check out this stay?");
    if (!ok) return;

    try {
      setLoading(true);
      setMsg("");

      const res = await hotelApi.post(`/checkout/${stayId}`, {});
      if (res.data?.success === false) {
        showMsg("❌ " + (res.data?.message || res.data?.error || "Checkout failed"));
        return;
      }

      showMsg("✅ Checkout successful");
      await refreshAll();
    } catch (e) {
      console.error(e);
      showMsg(
        "❌ " +
          (e?.response?.data?.message ||
            e?.response?.data?.error ||
            "Checkout failed.")
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.wrap}>
      <div style={styles.headerRow}>
        <div>
          <h2 style={{ margin: 0 }}>🏨 Front Desk Check-In</h2>
          <div style={styles.subHeader}>Select guest, assign room, add supplies, collect payment, then print receipt.</div>
        </div>
        <div style={styles.headerActions}>
  <button style={styles.btn} onClick={refreshAll} disabled={loading}>
    {loading ? "Loading..." : "Refresh"}
  </button>

  <button
    type="button"
    style={styles.primary}
    onClick={onOpenSupplies}
    disabled={loading}
  >
    Shop
  </button>

  <button style={styles.btn2} onClick={onBack}>
    Back
  </button>
</div>
      </div>

      {msg && <div style={styles.msg}>{msg}</div>}

      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Rooms</div>
          <div style={styles.statValue}>{rooms.length}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Available</div>
          <div style={styles.statValue}>{availableRooms.length}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Occupied</div>
          <div style={styles.statValue}>{occupiedRoomsCount}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Active Stays</div>
          <div style={styles.statValue}>{activeStays.length}</div>
        </div>
      </div>

      <div style={styles.grid}>
        <div style={styles.card}>
          <div style={styles.cardTitle}>New Check-In</div>

          <form
  onSubmit={(e) => {
    e.preventDefault();

    if (!canBuildDraft) {
      showMsg("⚠️ Guest, room and rate are required.");
      return;
    }

    if (activeGuestStay?.id) {
      showMsg("⚠️ Guest already has an active stay.");
      return;
    }

    setShowPaymentModal(true);
  }}
  style={styles.form}
>
            <div style={styles.stepTitle}>1. Guest details</div>
            <select
              style={styles.input}
              value={guestId || 0}
              onChange={(e) => {
                const v = Number(e.target.value);
                setGuestId(v);
                if (v === 0) setGuestName("");
                setGuestName("");
                setPhone("");
                setIdNumber("");
                setPin("");
              }}
              disabled={loading}
            >
              <option value={0}>Enter new guest...</option>
              {guests.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.full_name || g.guest_name}
                </option>
              ))}
            </select>

            <input
              style={styles.input}
              value={guestName}
              onChange={(e) => {
                setGuestName(e.target.value);
                if (e.target.value.trim()) setGuestId(0);
              }}
              placeholder="Guest name *"
              disabled={loading}
            />

            <div style={{ display: "flex", gap: 10 }}>
              <input
                style={{ ...styles.input, flex: 1 }}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Phone"
                disabled={loading}
              />
              <input
                style={{ ...styles.input, flex: 1 }}
                value={idNumber}
                onChange={(e) => setIdNumber(e.target.value)}
                placeholder="ID/Passport"
                disabled={loading}
              />

              <input
                style={{ ...styles.input, flex: 1 }}
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                placeholder="PIN Number"
                disabled={loading}
              />

            </div>

            {activeGuestStay && (
              <div style={styles.summaryBox}>
                <div style={styles.summaryTitle}>Guest Active Stay Details</div>
                <div><b>Guest:</b> {activeGuestStay.guest_name || "-"}</div>
                <div><b>Room:</b> {activeGuestStay.room_no || "-"}</div>
                <div><b>Room Total:</b> {Number(activeGuestStay.total_amount || 0).toFixed(2)}</div>
                <div><b>Shop Total:</b> {Number(activeGuestStay.supplies_total || 0).toFixed(2)}</div>
                <div><b>Grand Total:</b> {Number(activeGuestStay.grand_total || 0).toFixed(2)}</div>
                <div><b>Balance:</b> {Number(activeGuestStay.balance || 0).toFixed(2)}</div>
                <div style={{ marginTop: 8, color: "#b91c1c", fontWeight: 800 }}>
                  This guest already has an active stay.
                </div>
              </div>
            )}

            <div style={styles.stepTitle}>2. Room and stay details</div>

            <select
              style={styles.input}
              value={roomId || 0}
              onChange={(e) => setRoomId(Number(e.target.value))}
              disabled={loading}
            >
              <option value={0}>Select room...</option>
              {rooms.map((r) => {
                const occupied = isRoomOccupied(r);

                return (
                  <option
                    key={r.id}
                    value={r.id}
                    disabled={occupied || Number(r.is_active) !== 1}
                  >
                    {r.room_no} — {r.room_type || "Type?"} {occupied ? "(Occupied)" : ""}
                  </option>
                );
              })}
            </select>

            {selectedRoom && (
              <div style={selectedRoomOccupied ? styles.warningBox : styles.successBox}>
                <div><b>Selected room:</b> {selectedRoom.room_no} · {selectedRoom.room_type || "Room"}</div>
                <div><b>Base rate:</b> {Number(selectedRoom.base_rate || 0).toFixed(2)}</div>
                <div><b>Status:</b> {selectedRoomOccupied ? "Occupied" : "Available"}</div>
              </div>
            )}

            <input
              style={styles.input}
              value={roomType}
              onChange={(e) => setRoomType(e.target.value)}
              placeholder="Room type"
              disabled={loading || !!roomId}
            />

            <div style={{ display: "flex", gap: 10 }}>
              <input
                style={{ ...styles.input, flex: 1 }}
                value={nightlyRate}
                onChange={(e) => setNightlyRate(e.target.value)}
                placeholder="Nightly rate *"
                disabled={loading}
                inputMode="decimal"
              />
              <input
                style={{ ...styles.input, flex: 1 }}
                type="datetime-local"
                value={expectedCheckout}
                onChange={(e) => setExpectedCheckout(e.target.value)}
                disabled={loading}
              />
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <input
                style={{ ...styles.input, flex: 1 }}
                type="number"
                min="1"
                value={adults}
                onChange={(e) => setAdults(e.target.value)}
                placeholder="Adults"
                disabled={loading}
              />
              <input
                style={{ ...styles.input, flex: 1 }}
                type="number"
                min="0"
                value={children}
                onChange={(e) => setChildren(e.target.value)}
                placeholder="Children"
                disabled={loading}
              />
            </div>

            <label style={styles.checkRow}>
              <input
                type="checkbox"
                checked={breakfastIncluded}
                onChange={(e) => setBreakfastIncluded(e.target.checked)}
                disabled={loading}
              />
              <span>Breakfast included</span>
            </label>

            

            <div style={styles.paymentCard}>
              <div style={styles.summaryTitle}>Items</div>
              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <button
                  type="button"
                  style={styles.secondaryBtn}
                  onClick={openSupplyModal}
                  disabled={loading}
                >
                  Shop
                </button>
                <div style={{ fontWeight: 700, color: "#334155" }}>
                  Selected items: {cartLines.filter((x) => x.type === "supply").length}
                </div>
              </div>
            </div>

            <div style={styles.cartPanel}>
              <div style={styles.summaryTitle}>Shop Items</div>

              <div style={styles.tableWrap}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Item</th>
                      <th style={styles.th}>Qty</th>
                      <th style={styles.th}>Price</th>
                      <th style={styles.th}>Total</th>
                      <th style={styles.th}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {checkoutDraft.length === 0 ? (
                      <tr>
                        <td style={styles.td} colSpan={5}>No draft items.</td>
                      </tr>
                    ) : (
                      checkoutDraft.map((row, idx) => {
                        const editableIndex = idx - (breakfastLine ? 2 : 1);
                        const isEditableSupply = row.type === "supply";

                        return (
                          <tr key={`${row.type}-${idx}`}>
                            <td style={styles.td}>{row.name}</td>
                            <td style={styles.td}>
                              {isEditableSupply ? (
                                <input
                                  style={{ ...styles.input, minWidth: 80 }}
                                  type="number"
                                  min="1"
                                  value={row.qty}
                                  onChange={(e) => updateCartQty(editableIndex, e.target.value)}
                                />
                              ) : (
                                Number(row.qty || 0).toFixed(2)
                              )}
                            </td>
                            <td style={styles.td}>{Number(row.unit_price || 0).toFixed(2)}</td>
                            <td style={styles.td}>{Number(row.line_total || 0).toFixed(2)}</td>
                            <td style={styles.td}>
                              {isEditableSupply ? (
                                <button
                                  type="button"
                                  style={styles.removeBtn}
                                  onClick={() => removeCartLine(editableIndex)}
                                >
                                  Remove
                                </button>
                              ) : (
                                "—"
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              <div style={styles.paymentSummary}>
                <div><b>Shop Total:</b> {suppliesTotal.toFixed(2)}</div>
                <div><b>Draft Subtotal:</b> {cartSubTotal.toFixed(2)}</div>
              </div>

<button
  type="button"
  style={styles.primary}
  onClick={() => {
    if (!canBuildDraft) {
      showMsg("⚠️ Enter guest name, select room, and enter nightly rate first.");
      return;
    }

    if (activeGuestStay?.id) {
      showMsg("⚠️ Guest already has an active stay.");
      return;
    }

    setShowPaymentModal(true);
  }}
>
  Continue To Payment
</button>

            </div>

         
          </form>

        </div>

        <div style={styles.card}>
          <div style={styles.cardTitle}>Rooms (Occupied/Available)</div>

          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Room</th>
                  <th style={styles.th}>Type</th>
                  <th style={styles.th}>Occupied</th>
                  <th style={styles.th}>Action</th>
                </tr>
              </thead>
              <tbody>
                {rooms.length === 0 ? (
                  <tr>
                   <td style={styles.td} colSpan={4}>No rooms found.</td>
                  </tr>
                ) : (
                  rooms.map((r) => {
                    const occupied = isRoomOccupied(r);

                    return (
                      <tr key={r.id}>
                        <td style={styles.td}>{r.room_no}</td>
                        <td style={styles.td}>{r.room_type || "—"}</td>
                        <td style={styles.td}>
                          <span style={occupied ? styles.badgeDanger : styles.badgeSuccess}>
                            {occupied ? "Occupied" : "Available"}
                          </span>
                        </td>
                        <td style={styles.td}>
                          {!occupied && Number(r.is_active) === 1 ? (
                            <button
                              type="button"
                              style={styles.secondaryBtn}
                              onClick={() => setRoomId(Number(r.id))}
                              disabled={loading}
                            >
                              Use Room
                            </button>
                          ) : "—"}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: 14 }}>
            <div style={styles.cardTitle}>Active Stays Summary</div>

            <div style={styles.tableWrap}>
              <table style={styles.table}>
              <thead>
  <tr>
    <th style={styles.th}>Guest</th>
    <th style={styles.th}>Room</th>
    <th style={styles.th}>Room Total</th>
    <th style={styles.th}>Supplies</th>
    <th style={styles.th}>Grand Total</th>
    <th style={styles.th}>Balance</th>
    <th style={styles.th}>Action</th>
  </tr>
</thead>
                <tbody>
                  {activeStays.length === 0 ? (
                    <tr>
                      <td style={styles.td} colSpan={7}>No active stays found.</td>
                    </tr>
                  ) : (
                    activeStays.map((s) => (
                     <tr key={s.id}>
  <td style={styles.td}>{s.guest_name || "—"}</td>
  <td style={styles.td}>{s.room_no || "—"}</td>
  <td style={styles.td}>{Number(s.total_amount || 0).toFixed(2)}</td>
  <td style={styles.td}>{Number(s.supplies_total || 0).toFixed(2)}</td>
  <td style={styles.td}>{Number(s.grand_total || 0).toFixed(2)}</td>
  <td style={styles.td}>{Number(s.balance || 0).toFixed(2)}</td>

  <td style={styles.td}>
    <button
      style={styles.removeBtn}
      onClick={() => doCheckout(s.id)}
      disabled={loading}
    >
      Check Out
    </button>
  </td>
</tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
{showPaymentModal && (
  <div style={styles.modalOverlay}>
    <div style={styles.modalCard}>
      <div style={styles.modalHeader}>
        <h3 style={{ margin: 0 }}>💳 Payment Entry</h3>

        <button
          type="button"
          style={styles.btn}
          onClick={() => setShowPaymentModal(false)}
        >
          Close
        </button>
      </div>

      <div style={styles.summaryBox}>
        <div><b>Guest:</b> {guestName}</div>
        <div><b>Room:</b> {roomNo}</div>
        <div><b>Room Type:</b> {roomType}</div>
        <div><b>Total:</b> {cartSubTotal.toFixed(2)}</div>
      </div>

      <div style={{ marginTop: 12 }}>
        <label style={styles.label}>Cash</label>
        <input
          style={styles.input}
          type="number"
          value={cashAmount}
          onChange={(e) => setCashAmount(e.target.value)}
        />
      </div>

      <div style={{ marginTop: 12 }}>
        <label style={styles.label}>Mpesa</label>

        <div style={{ display: "flex", gap: 8 }}>
          <input
            style={{ ...styles.input, flex: 1 }}
            type="number"
            value={mpesaAmount}
            onChange={(e) => setMpesaAmount(e.target.value)}
          />

          <button
            type="button"
            style={styles.secondaryBtn}
            onClick={openMpesaModal}
          >
            Select Mpesa
          </button>
        </div>
      </div>

      <div style={{ marginTop: 12 }}>
        <label style={styles.label}>Bank</label>

        <input
          style={styles.input}
          type="number"
          value={bankAmount}
          onChange={(e) => setBankAmount(e.target.value)}
        />
      </div>

      <div style={styles.paymentSummary}>
        <div><b>Total:</b> {cartSubTotal.toFixed(2)}</div>
        <div><b>Paid:</b> {amountPaid.toFixed(2)}</div>

        <div
          style={
            balancePreview > 0
              ? styles.balanceDue
              : styles.balanceOk
          }
        >
          <b>Balance:</b> {balancePreview.toFixed(2)}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 10,
          marginTop: 15,
        }}
      >
        <button
          type="button"
          style={styles.btn}
          onClick={() => setShowPaymentModal(false)}
        >
          Cancel
        </button>

        <button
          type="button"
          style={styles.primary}
          onClick={() => {
            setShowPaymentModal(false);
            finalizeCheckIn({
              preventDefault: () => {},
            });
          }}
        >
          Save Check-In
        </button>
      </div>
    </div>
  </div>
)}
      {showSupplyModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalCard}>
            <div style={styles.modalHeader}>
              <h3 style={{ margin: 0 }}>Shop Module</h3>
              <button type="button" style={styles.btn} onClick={closeSupplyModal}>
                Close
              </button>
            </div>

            <div style={styles.modalTopBar}>
              <div style={{ flex: 2 }}>
                <label style={styles.label}>Item</label>
                <select
                  style={styles.input}
                  value={modalSelectedSupplyId}
                  onChange={(e) => setModalSelectedSupplyId(e.target.value)}
                >
                  <option value="">Select items...</option>
                  {supplies.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.item_name} - {Number(s.unit_price || 0).toFixed(2)} - Stock {Number(s.qty_on_hand || 0).toFixed(2)}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ width: 120 }}>
                <label style={styles.label}>Qty</label>
                <input
                  style={styles.input}
                  type="number"
                  min="1"
                  value={modalQty}
                  onChange={(e) => setModalQty(e.target.value)}
                />
              </div>

              <div style={{ alignSelf: "end" }}>
                <button
                  type="button"
                  style={styles.secondaryBtn}
                  onClick={addSupplyToModalCart}
                >
                  Add Item
                </button>
              </div>
            </div>

            {selectedModalSupply && (
              <div style={styles.modalHint}>
                <b>Selected:</b> {selectedModalSupply.item_name} | Price:{" "}
                {Number(selectedModalSupply.unit_price || 0).toFixed(2)} | Stock:{" "}
                {Number(selectedModalSupply.qty_on_hand || 0).toFixed(2)}
              </div>
            )}

            <div style={styles.modalCartBox}>
              <div style={styles.summaryTitle}></div>

              <div style={styles.tableWrap}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Item</th>
                      <th style={styles.th}>Qty</th>
                      <th style={styles.th}>Unit Price</th>
                      <th style={styles.th}>Line Total</th>
                      <th style={styles.th}>Action</th>  
                    </tr>
                  </thead>
                  <tbody>
                    {modalCart.length === 0 ? (
                      <tr>
                        <td style={styles.td} colSpan={5}>No items in supply cart.</td>
                      </tr>
                    ) : (
                      modalCart.map((row, idx) => (
                        <tr key={`${row.supply_id}-${idx}`}>
                          <td style={styles.td}>{row.name}</td>
                          <td style={styles.td}>
                            <input
                              style={{ ...styles.input, minWidth: 90 }}
                              type="number"
                              min="1"
                              value={row.qty}
                              onChange={(e) => updateModalCartQty(idx, e.target.value)}
                            />
                          </td>
                          <td style={styles.td}>{Number(row.unit_price || 0).toFixed(2)}</td>
                          <td style={styles.td}>{Number(row.line_total || 0).toFixed(2)}</td>
                          <td style={styles.td}>
                            <button
                              type="button"
                              style={styles.removeBtn}
                              onClick={() => removeModalCartLine(idx)}
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td style={styles.td} colSpan={3}>
                        <b>Total</b>
                      </td>
                      <td style={styles.td}>
                        <b>{modalCartTotal.toFixed(2)}</b>
                      </td>
                      <td style={styles.td}>—</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <div style={styles.modalActions}>
              <button
                type="button"
                style={styles.secondaryBtn}
                onClick={() => setModalCart([])}
              >
                Clear Items
              </button>
              <button
                type="button"
                style={styles.primary}
                onClick={applySupplyModalCart}
              >
                Apply Selection
              </button>
            </div>
          </div>
        </div>
      )}

      {showMpesaModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalCardWide}>
            <div style={styles.modalHeader}>
              <h3 style={{ margin: 0 }}>M-Pesa Transactions</h3>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  style={styles.secondaryBtn}
                  onClick={loadMpesaTransactions}
                  disabled={mpesaLoading}
                >
                  {mpesaLoading ? "Refreshing..." : "Refresh"}
                </button>
                <button
                  type="button"
                  style={styles.btn}
                  onClick={closeMpesaModal}
                >
                  Close
                </button>
              </div>
            </div>

            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>ID</th>
                    <th style={styles.th}>Account No</th>
                    <th style={styles.th}>Mpesa Code</th>
                    <th style={styles.th}>Amount</th>
                    <th style={styles.th}>Cust No</th>
                    <th style={styles.th}>First Name</th>
                    <th style={styles.th}>Middle Name</th>
                    <th style={styles.th}>Last Name</th>
                    <th style={styles.th}>Trans Date</th>
                    <th style={styles.th}>TDate</th>
                  </tr>
                </thead>
                <tbody>
                  {mpesaRows.length === 0 ? (
                    <tr>
                      <td style={styles.td} colSpan={10}>
                        {mpesaLoading ? "Loading transactions..." : "No M-Pesa transactions found."}
                      </td>
                    </tr>
                  ) : (
                    mpesaRows.map((row) => (
                      <tr
                        key={row.id}
                        style={styles.clickableRow}
                        onClick={() => pickMpesaTransaction(row)}
                        title="Click to use this amount"
                      >
                        <td style={styles.td}>{row.id}</td>
                        <td style={styles.td}>{row.account_no ?? "—"}</td>
                        <td style={styles.td}>{row.mpesa_code ?? "—"}</td>
                        <td style={styles.td}>{Number(row.amount || 0).toFixed(2)}</td>
                        <td style={styles.td}>{row.custno ?? "—"}</td>
                        <td style={styles.td}>{row.fname ?? "—"}</td>
                        <td style={styles.td}>{row.mname ?? "—"}</td>
                        <td style={styles.td}>{row.lname ?? "—"}</td>
                        <td style={styles.td}>{row.transdate ?? "—"}</td>
                        <td style={styles.td}>{row.tdate ?? "—"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: 10, color: "#475569", fontSize: 13, fontWeight: 700 }}>
              Click any row to transfer its amount into the M-Pesa textbox.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  wrap: {
    padding: 16,
    background: "#f8fafc",
    minHeight: "100vh",
  },

  headerRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 14,
    flexWrap: "wrap",
  },

  headerActions: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },

  subHeader: {
    marginTop: 4,
    color: "#64748b",
    fontSize: 13,
    fontWeight: 700,
  },

  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: 12,
    marginBottom: 14,
  },

  statCard: {
    background: "#ffffff",
    border: "1px solid #dbe3ea",
    borderRadius: 18,
    padding: 14,
    boxShadow: "0 10px 28px rgba(15,23,42,.06)",
  },

  statLabel: {
    fontSize: 12,
    color: "#64748b",
    fontWeight: 900,
    textTransform: "uppercase",
  },

  statValue: {
    marginTop: 6,
    fontSize: 26,
    color: "#0f172a",
    fontWeight: 950,
  },

  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(430px, 1fr))",
    gap: 14,
    alignItems: "start",
  },

  card: {
    background: "#ffffff",
    border: "1px solid #dbe3ea",
    borderRadius: 20,
    padding: 14,
    boxShadow: "0 12px 32px rgba(15,23,42,.07)",
  },

  cardTitle: {
    fontWeight: 950,
    marginBottom: 12,
    color: "#0f172a",
    fontSize: 17,
  },

  form: {
    display: "flex",
    flexDirection: "column",
    gap: 11,
  },

  label: {
    fontWeight: 850,
    color: "#0f172a",
    marginBottom: 5,
    display: "block",
  },

  input: {
    width: "100%",
    padding: 11,
    borderRadius: 13,
    border: "1px solid #cbd5e1",
    outline: "none",
    background: "#ffffff",
    boxSizing: "border-box",
    color: "#0f172a",
  },

  checkRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontWeight: 800,
    color: "#0f172a",
  },

  paymentCard: {
    background: "#f1f5f9",
    border: "1px solid #dbe3ea",
    borderRadius: 16,
    padding: 14,
  },

  paymentVertical: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },

  paymentSummary: {
    marginTop: 12,
    lineHeight: 1.8,
    fontWeight: 800,
    color: "#0f172a",
  },

  cartPanel: {
    background: "#95d0d4",
    border: "1px solid #dbe3ea",
    borderRadius: 16,
    padding: 14,
    marginTop: 6,
  },

  summaryBox: {
    background: "#f8fafc",
    border: "1px solid #dbe3ea",
    borderRadius: 16,
    padding: 14,
    lineHeight: 1.8,
    color: "#0f172a",
  },

  summaryTitle: {
    fontWeight: 950,
    marginBottom: 8,
    color: "#0f172a",
  },

  primary: {
    padding: "12px 16px",
    borderRadius: 13,
    border: "none",
    background: "#0f766e",
    color: "#ffffff",
    fontWeight: 950,
    cursor: "pointer",
    boxShadow: "0 8px 18px rgba(15,118,110,.22)",
  },

  secondaryBtn: {
    padding: "10px 13px",
    borderRadius: 13,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#0f172a",
    fontWeight: 850,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },

  removeBtn: {
    padding: "9px 11px",
    borderRadius: 11,
    border: "none",
    background: "#dc2626",
    color: "#ffffff",
    fontWeight: 850,
    cursor: "pointer",
    boxShadow: "0 8px 16px rgba(220,38,38,.18)",
  },

  btn: {
    padding: "10px 13px",
    borderRadius: 13,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#0f172a",
    fontWeight: 850,
    cursor: "pointer",
  },

  btn2: {
    padding: "10px 13px",
    borderRadius: 13,
    border: "1px solid #cbd5e1",
    background: "#f8fafc",
    color: "#0f172a",
    fontWeight: 850,
    cursor: "pointer",
  },

  msg: {
    marginBottom: 12,
    padding: 12,
    borderRadius: 14,
    background: "#ecfeff",
    border: "1px solid #67e8f9",
    color: "#155e75",
    fontWeight: 800,
  },

  tableWrap: {
    overflowX: "auto",
  },

  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
  },

  th: {
    textAlign: "left",
    fontSize: 12,
    color: "#334155",
    borderBottom: "1px solid #dbe3ea",
    padding: "10px 8px",
    whiteSpace: "nowrap",
    background: "#e2e8f0",
    position: "sticky",
    top: 0,
  },

  td: {
    padding: "10px 8px",
    borderBottom: "1px solid #e5e7eb",
    fontSize: 13,
    whiteSpace: "nowrap",
    color: "#0f172a",
  },

  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
    padding: 20,
  },

  modalCard: {
    width: "min(920px, 96vw)",
    maxHeight: "90vh",
    overflow: "auto",
    background: "#ffffff",
    borderRadius: 20,
    border: "1px solid #dbe3ea",
    boxShadow: "0 24px 70px rgba(15,23,42,.30)",
    padding: 18,
  },

  modalCardWide: {
    width: "min(1400px, 98vw)",
    maxHeight: "90vh",
    overflow: "auto",
    background: "#ffffff",
    borderRadius: 20,
    border: "1px solid #dbe3ea",
    boxShadow: "0 24px 70px rgba(15,23,42,.30)",
    padding: 18,
  },

  modalHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    marginBottom: 14,
  },

  modalTopBar: {
    display: "grid",
    gridTemplateColumns: "2fr 120px auto",
    gap: 10,
    alignItems: "end",
    marginBottom: 12,
  },

  modalHint: {
    background: "#ecfeff",
    border: "1px solid #67e8f9",
    borderRadius: 14,
    padding: 11,
    color: "#155e75",
    marginBottom: 12,
    fontWeight: 750,
  },

  modalCartBox: {
    background: "rgb(131, 177, 208)",
    border: "1px solid #dbe3ea",
    borderRadius: 16,
    padding: 14,
  },

  modalActions: {
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    marginTop: 14,
  },

  stepTitle: {
    fontWeight: 950,
    color: "#075985",
    background: "#e0f2fe",
    border: "1px solid #7dd3fc",
    padding: "10px 12px",
    borderRadius: 14,
  },

  successBox: {
    background: "#ecfdf5",
    border: "1px solid #86efac",
    borderRadius: 14,
    padding: 11,
    lineHeight: 1.7,
    color: "#065f46",
    fontWeight: 800,
  },

  warningBox: {
    background: "#fef2f2",
    border: "1px solid #fca5a5",
    borderRadius: 14,
    padding: 11,
    lineHeight: 1.7,
    color: "#991b1b",
    fontWeight: 800,
  },

  badgeSuccess: {
    display: "inline-block",
    padding: "5px 10px",
    borderRadius: 999,
    background: "#dcfce7",
    color: "#166534",
    fontWeight: 950,
    fontSize: 12,
  },

  badgeDanger: {
    display: "inline-block",
    padding: "5px 10px",
    borderRadius: 999,
    background: "#fee2e2",
    color: "#991b1b",
    fontWeight: 950,
    fontSize: 12,
  },

  balanceDue: {
    color: "#b91c1c",
    fontWeight: 950,
  },

  balanceOk: {
    color: "#166534",
    fontWeight: 950,
  },

  clickableRow: {
    cursor: "pointer",
  },
};
